import Vue from 'vue'
import './plugins/axios'
import App from './App.vue'
import './registerServiceWorker'
import router from './router'
import store from './store'
import i18n from './i18n'
import vuetify from './plugins/vuetify'
import '@fontsource/kanit'
import { msalInstance } from './authConfig'
import { msalPlugin } from './plugins/msalPlugin'
import { EventType } from '@azure/msal-browser'
// import { CustomNavigationClient } from '@/router/navigationClient'
import './plugins/dayjs'
import './plugins/apexChartErrorHandler'
// import VueGtm from '@gtm-support/vue2-gtm'
import 'nprogress/nprogress.css'
import VueHtmlToPaper from 'vue-html-to-paper';

const APP_VERSION = process.env.VUE_APP_VERSION || 'development'
const APP_VERSION_KEY = 'assettracking-app-version'
const CHUNK_RELOAD_KEY = 'assettracking-chunk-reload'

const reloadOnceForChunkError = () => {
  if (typeof window === 'undefined') return

  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1') {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  window.location.reload()
}

const clearStaleClientState = async () => {
  if (typeof window === 'undefined') return

  const previousVersion = window.localStorage.getItem(APP_VERSION_KEY)
  if (previousVersion === APP_VERSION) return

  window.localStorage.setItem(APP_VERSION_KEY, APP_VERSION)

  if (window.caches && typeof window.caches.keys === 'function') {
    const cacheNames = await window.caches.keys()
    await Promise.all(cacheNames.map(cacheName => window.caches.delete(cacheName)))
  }

  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map(registration => registration.unregister()))
  }
}

clearStaleClientState().catch((error) => {
  console.warn('Failed to clear stale client state:', error)
})

Vue.config.productionTip = false
Vue.config.silent = true // Suppress Vue warnings in production
Vue.config.devtools = false // Disable Vue devtools
Vue.config.debug = false // Disable Vue debug mode

// Disable webpack development error overlay
if (process.env.NODE_ENV === 'development') {
  const originalError = console.error
  const originalWarn = console.warn
  
  // Override console methods to suppress webpack overlay errors
  console.error = function(...args) {
    const message = String(args[0] || '')
    if (message.includes('Cannot read properties of undefined') ||
        message.includes('reading \'length\'') ||
        message.includes('ApexCharts') ||
        message.includes('webpack') ||
        message.includes('HMR') ||
        message.includes('overlay')) {
      return // Suppress these errors completely
    }
    originalError.apply(console, args)
  }
  
  console.warn = function(...args) {
    const message = String(args[0] || '')
    if (message.includes('Cannot read properties of undefined') ||
        message.includes('reading \'length\'') ||
        message.includes('ApexCharts')) {
      return // Suppress these warnings completely
    }
    originalWarn.apply(console, args)
  }
}

// Comprehensive error handling to prevent ALL runtime error pop-ups
Vue.config.errorHandler = function (err, vm, info) {
  // Log error for debugging but don't show to user
  console.warn('Vue Error (handled silently):', err.message || err, 'Info:', info)
  
  // Always return true to prevent default error behavior
  return true
}

// Enhanced error capturing
Vue.config.warnHandler = function (msg, vm, trace) {
  // Suppress all Vue warnings
  return
}

// Handle ALL unhandled promise rejections
window.addEventListener('unhandledrejection', event => {
  console.warn('Promise rejection (handled silently):', event.reason)

  const reasonMessage = String(event.reason && (event.reason.message || event.reason.name || event.reason) || '')
  if (reasonMessage.includes('ChunkLoadError') || reasonMessage.includes('Loading chunk')) {
    event.preventDefault()
    reloadOnceForChunkError()
    return
  }

  event.preventDefault() // Always prevent the default browser behavior
})

// Handle ALL JavaScript errors
window.addEventListener('error', event => {
  console.warn('JavaScript error (handled silently):', event.error || event.message)

  const errorMessage = String((event.error && (event.error.message || event.error.name)) || event.message || '')
  if (errorMessage.includes('ChunkLoadError') || errorMessage.includes('Loading chunk')) {
    event.preventDefault()
    reloadOnceForChunkError()
    return true
  }

  event.preventDefault() // Prevent default error popup
  return true
})

// Suppress webpack dev server error overlay
if (typeof window !== 'undefined' && window.__webpack_dev_server_client__) {
  const originalSendMessage = window.__webpack_dev_server_client__.sendMessage
  window.__webpack_dev_server_client__.sendMessage = function(message) {
    if (message.type === 'error' || message.type === 'warning') {
      return // Suppress webpack overlay messages
    }
    return originalSendMessage.call(this, message)
  }
}

// const navigationClient = new CustomNavigationClient(router)
// msalInstance.setNavigationClient(navigationClient)

// Default to using the first account if no account is active on page load
if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
}

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
    const account = event.payload.account
    msalInstance.setActiveAccount(account)
  }
})

const options = {
  name: '_blank',
  specs: [
    'fullscreen=yes',
    'titlebar=yes',
    'scrollbars=yes'
  ],
  styles: [
    'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
    'https://unpkg.com/kidlat-css/css/kidlat.css'
  ],
  timeout: 1000, // default timeout before the print window appears
  autoClose: true, // if false, the window will not close after printing
  windowTitle: window.document.title, // override the window title
}

Vue.use(VueHtmlToPaper, options)
Vue.use(msalPlugin, msalInstance)
// Vue.use(VueGtm, {
//   id: 'GTM-TSSBL4C', // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
//   defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
//   compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
//   nonce: '2726c7f26c', // Will add `nonce` to the script tag
//   enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
//   debug: true, // Whether or not display console logs debugs (optional)
//   loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
//   vueRouter: router, // Pass the router instance to automatically sync with router (optional)
//   trackOnNextTick: false// Whether or not call trackView in Vue.nextTick
// })

new Vue({
  router,
  store,
  i18n,
  vuetify,
  render: h => h(App)
}).$mount('#app')
